import React, { useCallback, useMemo, useRef } from 'react';
import { Portal } from "../Portal";
import { theme } from "../../theme";
import { DialogContext } from "./DialogContext";
import { Overlay } from "./Overlay";
import { noop } from "../../utils/noop";
import { useId } from '@react-aria/utils';
import { useBodyScrollLock, useFocusTrap, useToggleFocus } from "../../hooks";
import { useHideBody } from "./useHideBody";
import { Transition } from 'react-transition-group';
import { MODAL_ENTER_DURATION, MODAL_EXIT_DURATION } from "./animation-constants";
import styled from 'styled-components';
var TIMEOUT = {
  enter: MODAL_ENTER_DURATION,
  exit: MODAL_EXIT_DURATION
};
export function DialogContainer(_ref) {
  var initialFocusRef = _ref.initialFocusRef,
      isShowing = _ref.isShowing,
      children = _ref.children,
      _ref$onRequestClose = _ref.onRequestClose,
      onRequestClose = _ref$onRequestClose === void 0 ? noop : _ref$onRequestClose,
      _ref$zIndex = _ref.zIndex,
      zIndex = _ref$zIndex === void 0 ? theme.zIndices.modal : _ref$zIndex,
      _ref$enableClickOutsi = _ref.enableClickOutsideToClose,
      enableClickOutsideToClose = _ref$enableClickOutsi === void 0 ? true : _ref$enableClickOutsi;
  var id = useId();
  var ref = useRef(null);
  var rootRef = useRef(null); // trap focus inside our dialog

  var handleTab = useFocusTrap(ref); // manage focus

  useToggleFocus(initialFocusRef || ref, isShowing); // lock body scrolling

  useBodyScrollLock({
    enabled: isShowing
  }); // set aria-hidden to all root elements besides this one when showing

  useHideBody(rootRef, isShowing); // close dialog when escape is pressed

  var onKeyDown = useCallback(function (e) {
    handleTab(e);

    if (e.key === 'Escape') {
      // if we have a dialog within a dialog, only close
      // the first one (todo: potentially use context here instead
      // of event propagation)
      e.stopPropagation();
      onRequestClose(e);
    }
  }, [onRequestClose, handleTab]);
  var dialogContext = useMemo(function () {
    return {
      onRequestClose: onRequestClose,
      id: id
    };
  }, [onRequestClose, id]);

  function onContainerClick(e) {
    if (e.target === rootRef.current && enableClickOutsideToClose) {
      onRequestClose(e);
    }
  }

  return /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement(DialogContext.Provider, {
    value: dialogContext
  }, /*#__PURE__*/React.createElement(Overlay, {
    zIndex: zIndex,
    isShowing: isShowing
  }), /*#__PURE__*/React.createElement(Transition, {
    in: isShowing,
    mountOnEnter: true,
    unmountOnExit: true,
    timeout: TIMEOUT
  }, function (state) {
    return /*#__PURE__*/React.createElement(FixedContainer, {
      onClick: onContainerClick,
      ref: rootRef,
      onKeyDown: onKeyDown,
      $zIndex: zIndex + 1
    }, children(ref, state, id));
  })));
}
var FixedContainer = styled.div.withConfig({
  displayName: "DialogContainer__FixedContainer",
  componentId: "vapor__sc-1ufoo1f-0"
})(["position:fixed;top:0;left:0;right:0;bottom:0;overflow-y:scroll;z-index:", ";display:flex;justify-content:center;align-items:flex-start;"], function (p) {
  return p.$zIndex;
});